import { useCallback, useEffect, useState } from 'react'
import {
  Loader2, Plus, Trash2, AlertCircle, GripVertical, ChevronUp, ChevronDown, Lightbulb,
} from 'lucide-react'
import { apiGet, postJson, patchJson, apiDelete } from '../../lib/api.js'
import ConfirmDialog from '../ConfirmDialog.jsx'
import { POSITIONS } from '../../lib/employee.js'
import {
  QUESTION_TYPES, blankQuestion, TONE_CLASS, RELATIONS, relationLabel, normalizeQuestions,
} from '../../lib/evaluation.js'

// ชุดคำถามประเมิน — the question-set builder. HR/Admin only.
//
// Two rules baked into the shape of a question and worth not "simplifying" away:
//
//   * `id` is a canonical token generated once and never shown to respondents. Answers are
//     stored against it, so rewording a question later keeps its history intact. Editing the
//     label is safe; editing the id would orphan every answer ever given.
//
//   * dropdown option scores are on a fixed 0-100 scale. That is what lets the server score
//     a mixed form without any max-of-options maths (see score_evaluation_task).
//
// Editing a form never disturbs a round in flight — each dispatched task carries its own
// frozen snapshot.

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'
const LABEL = 'block text-[12.5px] font-medium text-slate-500 mb-1'

export default function EvaluationFormBuilder() {
  const [forms, setForms] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [editing, setEditing] = useState(null)
  const [confirmDel, setConfirmDel] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const f = await apiGet('/api/hr/evaluation/form')
      setForms(Array.isArray(f) ? f : [])
    } catch (e) {
      setErr(e?.message || 'โหลดชุดคำถามไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function remove(f) {
    setConfirmDel(null)
    try {
      await apiDelete(`/api/hr/evaluation/form/${f.id}`)
      await load()
    } catch (e) {
      setErr(e?.message || 'ลบชุดคำถามไม่สำเร็จ')
    }
  }

  if (editing) {
    return (
      <FormEditor
        form={editing}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); load() }}
      />
    )
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4">
        <p className="text-[13px] text-slate-500 max-w-2xl">
          ชุดคำถามที่นำไปผูกกับเกณฑ์ · หนึ่งชุดใช้ซ้ำได้หลายตำแหน่ง
        </p>
        <button
          type="button"
          onClick={() => setEditing({ title: '', description: '', status: 'draft', questions: [] })}
          className="px-4 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 shadow-sm inline-flex items-center gap-2 flex-none"
        >
          <Plus size={15} strokeWidth={2} /> สร้างชุดคำถาม
        </button>
      </div>

      {err && <div className="text-[13px] text-rose-500 mb-3">{err}</div>}

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden divide-y divide-slate-50">
        {loading && (
          <div className="px-4 py-12 text-center text-slate-400"><Loader2 size={16} className="animate-spin inline mr-2" />กำลังโหลด…</div>
        )}
        {!loading && !forms.length && (
          <div className="px-4 py-12 text-center text-slate-400">ยังไม่มีชุดคำถามประเมิน</div>
        )}
        {!loading && forms.map((f) => (
          <div key={f.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-brand-50/30">
            <button type="button" onClick={() => setEditing(f)} className="min-w-0 flex-1 text-left">
              <div className="text-[14px] font-medium text-slate-900 truncate">{f.title}</div>
              <div className="text-[12px] text-slate-400 mt-0.5 truncate">
                {(f.questions || []).length} คำถาม
                {f.description ? ` · ${f.description}` : ''}
              </div>
              {/* What this set is FOR. Shown in the list because the title alone does not say
                  it, and picking the wrong set for a rule is the easy mistake to make. An
                  empty declaration genuinely means "any", so it is spelled out rather than
                  left blank. */}
              <div className="flex flex-wrap items-center gap-1 mt-1.5">
                <span className="text-[11px] text-slate-400 mr-0.5">ใช้ประเมิน</span>
                {(f.subject_positions || []).length
                  ? f.subject_positions.map((p) => (
                    <span key={p} className="inline-flex rounded-md bg-slate-100 text-slate-600 px-1.5 py-0.5 text-[11px]">{p}</span>
                  ))
                  : <span className="text-[11px] text-slate-400 italic">ทุกตำแหน่ง</span>}

                <span className="text-slate-200 mx-1">·</span>

                <span className="text-[11px] text-slate-400 mr-0.5">ผู้ประเมิน</span>
                {(f.evaluator_relations || []).length
                  ? f.evaluator_relations.map((r) => (
                    <span key={r} className="inline-flex rounded-md bg-brand-50 text-brand-700 px-1.5 py-0.5 text-[11px]">{relationLabel(r)}</span>
                  ))
                  : <span className="text-[11px] text-slate-400 italic">ทุกกลุ่ม</span>}
              </div>
            </button>
            <span className={`inline-flex rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 flex-none ${
              f.status === 'active' ? TONE_CLASS.emerald : f.status === 'retired' ? TONE_CLASS.slate : TONE_CLASS.amber
            }`}>
              {f.status === 'active' ? 'ใช้งาน' : f.status === 'retired' ? 'เลิกใช้' : 'ฉบับร่าง'}
            </span>
            <button
              type="button"
              onClick={() => setConfirmDel(f)}
              className="p-1.5 rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50 flex-none"
              title="เลิกใช้ชุดคำถามนี้"
            >
              <Trash2 size={14} strokeWidth={1.75} />
            </button>
          </div>
        ))}
      </div>

      {confirmDel && (
        <ConfirmDialog
          title="เลิกใช้ชุดคำถามนี้?"
          message="แบบประเมินที่ส่งไปแล้วจะไม่ได้รับผลกระทบ เพราะแต่ละชุดเก็บสำเนาคำถามของตัวเองไว้ · ถ้ายังมีเกณฑ์ที่ผูกอยู่ ระบบจะไม่ยอมให้ลบ"
          confirmLabel="เลิกใช้"
          tone="danger"
          onConfirm={() => remove(confirmDel)}
          onCancel={() => setConfirmDel(null)}
        />
      )}
    </div>
  )
}

function FormEditor({ form, onCancel, onSaved }) {
  const [v, setV] = useState({
    ...form,
    questions: Array.isArray(form.questions) ? form.questions : [],
  })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const set = (patch) => setV((p) => ({ ...p, ...patch }))
  const setQ = (i, patch) => setV((p) => ({
    ...p,
    questions: p.questions.map((q, j) => (j === i ? { ...q, ...patch } : q)),
  }))
  const move = (i, d) => setV((p) => {
    const next = [...p.questions]
    const j = i + d
    if (j < 0 || j >= next.length) return p
    ;[next[i], next[j]] = [next[j], next[i]]
    return { ...p, questions: next }
  })

  async function save() {
    setBusy(true)
    setErr('')
    try {
      const body = {
        title: v.title,
        description: v.description || '',
        // Every question is forced required here rather than per-question in the editor.
        questions: normalizeQuestions(v.questions),
        subject_positions: v.subject_positions || [],
        evaluator_relations: v.evaluator_relations || [],
        status: v.status || 'draft',
      }
      if (v.id) await patchJson(`/api/hr/evaluation/form/${v.id}`, body)
      else await postJson('/api/hr/evaluation/form', body)
      onSaved()
    } catch (e) {
      setErr(e?.message || 'บันทึกไม่สำเร็จ')
      setBusy(false)
    }
  }

  return (
    <div>
      <button type="button" onClick={onCancel} className="text-[13px] text-slate-500 hover:text-slate-900 mb-3">
        ← กลับไปรายการชุดคำถาม
      </button>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className={LABEL}>ชื่อชุดคำถาม</label>
            <input className={FIELD} value={v.title} onChange={(e) => set({ title: e.target.value })}
              placeholder="เช่น แบบประเมินเพื่อนร่วมงาน — Counter" />
          </div>
          <div>
            <label className={LABEL}>สถานะ</label>
            <select className={FIELD} value={v.status} onChange={(e) => set({ status: e.target.value })}>
              <option value="draft">ฉบับร่าง</option>
              <option value="active">ใช้งาน</option>
              <option value="retired">เลิกใช้</option>
            </select>
          </div>
        </div>
        <div className="mt-3">
          <label className={LABEL}>คำอธิบาย (แสดงเหนือคำถามข้อแรก)</label>
          <input className={FIELD} value={v.description || ''} onChange={(e) => set({ description: e.target.value })}
            placeholder="เช่น ประเมินจากสิ่งที่เห็นจริงในช่วงรอบประเมิน" />
        </div>

        {/* What this form is FOR. Not enforced at dispatch — evaluation_rule is still what
            binds a form to a position and a group — but it filters the form picker on the
            เกณฑ์ screen, so a Counter form cannot be attached to a ผู้จัดการ rule by mistake.
            Leaving either empty means "any". */}
        <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-100">
          <div>
            <label className={LABEL}>ใช้ประเมินตำแหน่ง</label>
            <TagPicker
              options={POSITIONS}
              values={v.subject_positions || []}
              onChange={(next) => set({ subject_positions: next })}
              emptyLabel="ทุกตำแหน่ง"
            />
          </div>
          <div>
            <label className={LABEL}>ผู้ประเมินคือ</label>
            {/* `custom` is not offered here. It is not a group a question set is written
                for — it is the always-available slot for hand-picking an extra evaluator
                when a round is assembled, so declaring a form "for กำหนดเอง" would say
                nothing. */}
            <TagPicker
              options={RELATIONS.filter((r) => r.value !== 'custom').map((r) => r.value)}
              labelOf={(x) => RELATIONS.find((r) => r.value === x)?.label || x}
              values={v.evaluator_relations || []}
              onChange={(next) => set({ evaluator_relations: next })}
              emptyLabel="ทุกกลุ่ม"
            />
          </div>
        </div>
      </div>

      <div className="flex items-start gap-2 text-[12.5px] text-sky-800 bg-sky-50 border border-sky-200 rounded-xl px-3 py-2.5 mb-4">
        <Lightbulb size={14} className="mt-0.5 flex-none" />
        <div>
          เขียนคำถามจาก <strong>เหตุการณ์ที่สังเกตได้</strong> ไม่ใช่ความรู้สึกรวม และ
          <strong> อย่าใช้คำถามเชิงลบสลับขั้ว</strong> (reverse-worded) — วัดผลได้แย่ลงในภาษาไทย ·
          ถ้าต้องการให้แต่ละระดับมีความหมายชัดเจน ให้ใช้ชนิด “ตัวเลือก” แทน Scale
        </div>
      </div>

      <div className="space-y-3">
        {v.questions.map((q, i) => (
          <QuestionEditor
            key={q.id}
            q={q}
            index={i}
            onChange={(patch) => setQ(i, patch)}
            onRemove={() => set({ questions: v.questions.filter((_, j) => j !== i) })}
            onMove={(d) => move(i, d)}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={() => set({ questions: [...v.questions, blankQuestion(v.questions.length)] })}
        className="mt-3 w-full px-4 py-3 rounded-2xl border border-dashed border-slate-300 text-[13.5px] font-medium text-slate-500 hover:border-brand-300 hover:text-brand-700 hover:bg-brand-50/30 inline-flex items-center justify-center gap-2"
      >
        <Plus size={15} strokeWidth={2} /> เพิ่มคำถาม
      </button>

      {err && (
        <div className="flex items-start gap-2 text-[13px] text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2.5 mt-4">
          <AlertCircle size={14} className="mt-0.5 flex-none" />{err}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-5">
        <button type="button" onClick={onCancel} className="px-4 py-2.5 rounded-xl text-[14px] font-medium text-slate-600 hover:bg-slate-50">ยกเลิก</button>
        <button
          type="button"
          disabled={busy || !v.title.trim()}
          onClick={save}
          className="px-4 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 shadow-sm disabled:opacity-40 inline-flex items-center gap-2"
        >
          {busy && <Loader2 size={15} className="animate-spin" />} บันทึกชุดคำถาม
        </button>
      </div>
    </div>
  )
}

// Multi-select rendered as toggleable tags rather than a <select multiple> — the option
// sets here are short and fixed, and a row of tags shows the whole answer at a glance
// instead of hiding it behind a scroll box.
function TagPicker({ options, values, onChange, labelOf = (x) => x, emptyLabel }) {
  const on = new Set(values || [])
  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const active = on.has(o)
          return (
            <button
              key={o}
              type="button"
              onClick={() => {
                const next = new Set(on)
                if (next.has(o)) next.delete(o)
                else next.add(o)
                onChange([...next])
              }}
              className={`rounded-full px-2.5 py-1 text-[12.5px] font-medium border transition-colors ${
                active
                  ? 'border-brand-400 bg-brand-50 text-brand-700'
                  : 'border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              {labelOf(o)}
            </button>
          )
        })}
      </div>
      {!on.size && <p className="text-[11.5px] text-slate-400 mt-1.5">ไม่เลือก = {emptyLabel}</p>}
    </div>
  )
}

function QuestionEditor({ q, index, onChange, onRemove, onMove }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      {/* The question's `id` token is deliberately NOT shown. It is an internal key that
          answers are stored against — never edited, never meaningful to the author — and
          putting it on screen only invites someone to try to "fix" it. */}
      <div className="px-4 py-2.5 bg-slate-50/60 border-b border-slate-100 flex items-center gap-2">
        <GripVertical size={14} className="text-slate-300" />
        <span className="text-[12px] font-medium text-slate-400">ข้อ {index + 1}</span>
        <div className="ml-auto flex items-center gap-0.5">
          <button type="button" onClick={() => onMove(-1)} className="p-1 rounded text-slate-300 hover:text-slate-700 hover:bg-slate-100"><ChevronUp size={14} /></button>
          <button type="button" onClick={() => onMove(1)} className="p-1 rounded text-slate-300 hover:text-slate-700 hover:bg-slate-100"><ChevronDown size={14} /></button>
          <button type="button" onClick={onRemove} className="p-1 rounded text-slate-300 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></button>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* No weight input: every question counts the same. No section input either — one
            flat list reads better than half-filled headings. The scorer still reads
            `question.weight` (defaulting to 1) and old forms keep any section they carry,
            so nothing already authored changes meaning. */}
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className={LABEL}>คำถาม</label>
            <input className={FIELD} value={q.label || ''} onChange={(e) => onChange({ label: e.target.value })}
              placeholder="เช่น เมื่องานหน้าเคาน์เตอร์แน่น เพื่อนคนนี้เข้ามาช่วยรับงานต่อบ่อยแค่ไหน" />
          </div>
          <div>
            <label className={LABEL}>ชนิด</label>
            <select className={FIELD} value={q.type} onChange={(e) => onChange({ type: e.target.value })}>
              {QUESTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>

        {/* Nothing to configure for `scale`: it is a fixed 1-5 on every question, so the
            editor shows no controls for it at all. */}

        {q.type === 'dropdown' && (
          <div>
            <label className={LABEL}>ตัวเลือก — คะแนน 0-100 ต่อข้อ</label>
            <div className="space-y-1.5">
              {(q.options || []).map((o, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <input
                    className={FIELD}
                    value={o.label || ''}
                    placeholder="ข้อความตัวเลือก"
                    onChange={(e) => onChange({
                      options: q.options.map((x, j) => (j === oi
                        ? { ...x, label: e.target.value, value: x.value || `opt${oi + 1}` }
                        : x)),
                    })}
                  />
                  <input
                    type="number" min={0} max={100}
                    className="w-24 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400"
                    value={o.score ?? 0}
                    onChange={(e) => onChange({
                      options: q.options.map((x, j) => (j === oi ? { ...x, score: Number(e.target.value) } : x)),
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => onChange({ options: q.options.filter((_, j) => j !== oi) })}
                    className="p-1.5 rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50 flex-none"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => onChange({
                options: [...(q.options || []), { value: `opt${(q.options || []).length + 1}`, label: '', score: 0 }],
              })}
              className="mt-2 text-[13px] text-brand-700 hover:underline"
            >
              + เพิ่มตัวเลือก
            </button>
          </div>
        )}

        {/* No ต้องตอบ toggle: every question is required. An optional question in a staff
            evaluation is one that quietly gets skipped, and a score computed over "whichever
            questions this evaluator felt like answering" is not comparable between people.
            ไม่มีข้อมูล stays available — that is an explicit, recorded "I did not see this",
            which is a different statement from silence and is excluded from the score. */}
        <div className="flex items-center gap-4 pt-1">
          {q.type !== 'text' && (
            <label className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-600 cursor-pointer">
              <input type="checkbox" className="accent-brand-600" checked={!!q.allow_na}
                onChange={(e) => onChange({ allow_na: e.target.checked })} />
              ให้เลือก “ไม่มีข้อมูล” ได้
              <span className="text-slate-400">(ไม่ถูกนำมาคิดคะแนน)</span>
            </label>
          )}
        </div>
      </div>
    </div>
  )
}

